import { normalizeSportsnewListRecovery } from './sportsnewListPatch.js';

export const SPORTSNEW_LIST_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS_NONEMPTY: 'success_nonempty',
  SUCCESS_EMPTY: 'success_empty',
  ERROR: 'error',
});

const requestValue = (value, fallback = '') => (
  value == null || value === '' ? fallback : String(value)
);

const fixtureRowsFromResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.rows)) return data.rows;
  if (data && Array.isArray(data.fixtures)) return data.fixtures;
  if (data && Array.isArray(data.data)) return data.data;
  return null;
};

const responseCode = (response) => {
  const source = response && typeof response === 'object' ? response : {};
  const nested = source.data && typeof source.data === 'object' && !Array.isArray(source.data)
    ? source.data
    : {};
  return String(
    source.code ?? source.errorCode ?? source.error_code
    ?? nested.code ?? nested.errorCode ?? nested.error_code ?? ''
  ).trim().toUpperCase();
};

const responseRetryAfterMs = (response) => {
  if (response?.retryAfterMs == null) return null;
  const value = Number(response.retryAfterMs);
  return Number.isFinite(value) && value >= 0 ? value : null;
};

export const sportsnewListRequestKey = (params = {}) => [
  requestValue(params.mode),
  requestValue(params.scope),
  requestValue(params.sport, 'all'),
  requestValue(params.league_id),
  requestValue(params.game_title_id),
  requestValue(params.tournament_id),
  requestValue(params.page, '1'),
  requestValue(params.limit, '30'),
].join('|');

export const isSportsnewListViewReady = ({ activeKey, resolvedKey } = {}) => (
  typeof activeKey === 'string'
  && activeKey.length > 0
  && activeKey === resolvedKey
);

const nonNegativeSafeInteger = value => {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const positiveSafeInteger = (value, fallback) => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

/** One accepted REST generation consumed atomically by cards, totals and rail filters. */
export const buildSportsnewAcceptedListGeneration = ({
  fixtures = [],
  data,
  response,
  params = {},
  pageFallback = 1,
  pageSizeFallback = 30,
  membershipRevision = null,
} = {}) => {
  const rows = Array.isArray(fixtures) ? fixtures : [];
  const totalItems = nonNegativeSafeInteger(
    data?.total ?? data?.totalCount ?? response?.total,
  );
  const limit = positiveSafeInteger(
    data?.limit ?? params.limit,
    positiveSafeInteger(pageSizeFallback, 30),
  );
  const totalPages = positiveSafeInteger(
    data?.totalPages ?? response?.totalPages,
    Math.max(1, Math.ceil(Number(totalItems ?? rows.length) / limit)),
  );
  const page = positiveSafeInteger(params.page, positiveSafeInteger(pageFallback, 1));

  return Object.freeze({
    fixtures: rows,
    totalItems,
    totalPages,
    snapshotComplete: page === 1
      && totalPages === 1
      && totalItems != null
      && rows.length === totalItems,
    membershipRevision,
    requestKey: sportsnewListRequestKey(params),
    listCacheVersion: data?.listCacheVersion ?? null,
  });
};


export const classifySportsnewListResponse = (response) => {
  const source = response && typeof response === 'object' ? response : {};
  const httpStatus = Number(source.httpStatus);
  const retryAfterMs = responseRetryAfterMs(source);
  const code = responseCode(source);
  const isHttpSuccess = Number.isFinite(httpStatus) && httpStatus >= 200 && httpStatus < 300;
  // Auth, rate limiting and cancellation never borrow transition retries.
  if ([401, 403, 429].includes(httpStatus) || source.cancelled === true
      || source.canceled === true || code === 'ERR_CANCELED' || code === 'ABORT_ERR') {
    return { status: SPORTSNEW_LIST_STATUS.ERROR, rows: null, retryable: false,
      retryAfterMs: null, error: source.message || 'Failed to load fixtures', reason: 'terminal' };
  }
  const rows = fixtureRowsFromResponse(source.data);

  if (isHttpSuccess && source.status !== false && source.error == null && rows) {
    return {
      status: rows.length > 0
        ? SPORTSNEW_LIST_STATUS.SUCCESS_NONEMPTY
        : SPORTSNEW_LIST_STATUS.SUCCESS_EMPTY,
      rows,
      retryable: false,
      retryAfterMs: null,
      error: null,
    };
  }

  return {
    status: SPORTSNEW_LIST_STATUS.ERROR,
    rows: null,
    retryable: httpStatus !== 403 && httpStatus !== 429 && (
      code === 'SPORTSNEW_LIST_TRANSITION' || retryAfterMs !== null
    ),
    retryAfterMs,
    error: source.message || 'Failed to load fixtures',
  };
};

export const classifySportsnewAggregateResponse = (response = {}) => {
  const httpStatus = Number(response?.httpStatus);
  const hasHttpStatus = Number.isFinite(httpStatus);
  const isLogicalSuccess = response?.status !== false
    && response?.networkError !== true
    && response?.error == null
    && response?.data != null;
  const isHttpSuccess = isLogicalSuccess && (
    !hasHttpStatus || (httpStatus >= 200 && httpStatus < 300)
  );
  const code = responseCode(response);

  if (isHttpSuccess) {
    return { success: true, retryable: false, reason: 'success' };
  }

  const retryable = httpStatus !== 403 && (
    httpStatus === 503
    || response?.networkError === true
    || code === 'SPORTSNEW_LIST_TRANSITION'
    || code === 'RM_FALLBACK_UNAVAILABLE'
  );
  return {
    success: false,
    retryable,
    reason: response?.networkError === true ? 'network' : (code || 'transport'),
  };
};

export const shouldRetrySportsnewList = (classification, retryCount = 0) => Boolean(
  classification?.status === SPORTSNEW_LIST_STATUS.ERROR
  && classification?.retryable
  && Number(retryCount) < 1
);

export const sportsnewListRetryDelayMs = (classification) => {
  const delay = Number(classification?.retryAfterMs);
  return Number.isFinite(delay) && delay > 0 ? delay : 0;
};

export const createSportsnewListRequestState = ({
  requestKey = '',
  status = SPORTSNEW_LIST_STATUS.IDLE,
  error = null,
  hasLastGood = false,
} = {}) => ({ requestKey, status, error, hasLastGood: Boolean(hasLastGood) });

export const sportsnewListRowsForKey = (snapshots, requestKey) => {
  const rows = snapshots instanceof Map ? snapshots.get(requestKey)?.rows : null;
  return Array.isArray(rows) ? rows : [];
};

export const isSportsnewListResponseCurrent = ({
  activeRequestKey,
  activeRequestToken,
  responseRequestKey,
  responseRequestToken,
} = {}) => (
  activeRequestKey === responseRequestKey
  && activeRequestToken === responseRequestToken
);

export const sportsnewListUiState = ({ status, rowCount = 0 } = {}) => {
  const count = Math.max(Number(rowCount) || 0, 0);
  return {
    showInitialLoading: (
      status === SPORTSNEW_LIST_STATUS.IDLE || status === SPORTSNEW_LIST_STATUS.LOADING
    ) && count === 0,
    showError: status === SPORTSNEW_LIST_STATUS.ERROR,
    showEmpty: status === SPORTSNEW_LIST_STATUS.SUCCESS_EMPTY,
    showData: count > 0,
  };
};

export { fixtureRowsFromResponse as fixtureRowsFromListResponse };

// Explicit display-only contract. A malformed tagged response must never fall
// through to the ordinary REST path and gain observation/unlock authority.
export const normalizeSportsnewListPresentation = (data, params = {}) => {
  const value = data?.presentation;
  if (!value || value.schema !== 'sportsnew-list-presentation/v1'
    || value.hotStateAuthority !== false || typeof value.windowKey !== 'string' || !value.windowKey
    || typeof value.structuralRevision !== 'string' || !/^(0|[1-9][0-9]*)$/.test(value.structuralRevision)
    || typeof value.room !== 'string' || !value.room
    || !Number.isSafeInteger(value.observedAt) || value.observedAt < 0) return null;
  const sub = data.subscription;
  if (!sub || !sub.filterHash || sub.mode !== params.mode || sub.scope !== params.scope
    || String(sub.sport || 'all') !== String(params.sport || 'all')
    || value.room !== (sub.room || `sportsnew:list:${sub.mode}:${sub.filterHash}`)
    || Number(data.page) !== Number(params.page || 1)
    || Number(data.limit) !== Number(params.limit || 30)) return null;
  for (const [camel, snake] of [['leagueId', 'league_id'], ['gameTitleId', 'game_title_id'], ['tournamentId', 'tournament_id']]) {
    if (String(sub[camel] || '') !== String(params[snake] || '')) return null;
  }
  const cursor = input => input && typeof input.epoch === 'string' && input.epoch
    && Number.isSafeInteger(input.sequence) && input.sequence >= 0
    ? { epoch: input.epoch, sequence: input.sequence } : null;
  const baseline = cursor(value.cursor);
  if (value.cursor != null && !baseline) return null;
  const replay = value.replay;
  const end = cursor(replay?.cursor);
  let recovered = null;
  if (baseline && end && baseline.epoch === end.epoch && replay?.gap === false
    && Array.isArray(replay.deltas) && replay.deltas.every(delta =>
      delta?.streamEpoch === baseline.epoch && delta?.patch
      && (!delta.patch.mode || delta.patch.mode === sub.mode)
      && (!delta.patch.scope || delta.patch.scope === sub.scope)
      && (!delta.patch.filterHash || delta.patch.filterHash === sub.filterHash)
      && (!delta.patch.sport || sub.sport === 'all' || delta.patch.sport === sub.sport))) {
    recovered = normalizeSportsnewListRecovery({ data: {
      room: value.room, gap: false, latestSequence: end.sequence, deltas: replay.deltas,
    } }, { room: value.room, since: baseline.sequence });
  }
  // Missing/truncated/restarted hot streams degrade to the canonical display
  // baseline. They never turn a structurally fenced list into a 503/error.
  return { ...value, cursor: recovered ? end : baseline,
    baselineCursor: baseline, deltas: recovered?.deltas ?? [], gap: !recovered };
};

// Event-driven shared-list recovery only: an HTTP gap never recursively polls.
export const canRecoverSportsnewPresentation = ({ previous, key, now, eventDriven = false }) => (
  !previous || previous.key !== key || (eventDriven && now - previous.startedAt >= 5000)
);
